import { useAppStore } from "@/global/store";
import { useMouseOver } from "@/shared/base/hooks/useMouseOver";
import { Box, Stack, useTheme, Theme, Button } from "@mui/material";
import React, { useEffect } from "react";
import { SearchBar } from "./SearchBar";
import { FilterButton, renderButtonLabel } from "./FilterButton";
import { FloatingBar } from "@/shared/base/components/FloatingBar/FloatingBar";
import { v4 as uuid } from "uuid";
import { FilterPanels } from "./types";
import { debounce } from "@/global/utils/utils";
import { useElementScroll } from "@/shared/base/hooks/useElementScroll";
import OutsideClickHandler from "react-outside-click-handler";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

interface DesktopFilterProps {
  selectedFilter: string;
  onSelect: (a: string) => void;
  onClearFilters?: () => void;
  filterPanels: FilterPanels;
  isDisabledInteractiveToggle: boolean;
  isClearFiltersButtonVisible?: boolean;
}

export const DesktopFilters = React.memo((props: DesktopFilterProps) => {
  const teamConfig = useTeamConfig();
  const {
    selectedFilter,
    onSelect,
    filterPanels,
    onClearFilters,
    isClearFiltersButtonVisible,
  } = props;
  const theme = useTheme();
  const searchBoxRef = React.useRef(null);
  const gameFilters = useAppStore((s) => s.data.gameFilters);
  const styles = useStyles(theme);
  const interactiveToggle = useFilterInteraction(props);

  return (
    <OutsideClickHandler onOutsideClick={() => onSelect("")}>
      <Box sx={styles.searchBoxWrapper}>
        <SearchBar
          ref={searchBoxRef}
          onClearFilters={onClearFilters}
          onMouseEnter={() => interactiveToggle.setIsSearchInteractive(true)}
          onMouseLeave={() => interactiveToggle.setIsSearchInteractive(false)}
          isBottomPanelOpen={!!selectedFilter}
          isHidden={filterPanels.every((itm) => itm.isHidden)}
          isClearFiltersButtonVisible={isClearFiltersButtonVisible}
          renderButtons={() => {
            return (
              <Stack spacing={1} direction={"row"} ml={0} pr={2}>
                {filterPanels.map((itm) => {
                  return (
                    <FilterButton
                      key={uuid()}
                      name={itm.name}
                      label={renderButtonLabel(
                        itm.name,
                        gameFilters,
                        teamConfig
                      )}
                      isSelected={itm.name == selectedFilter}
                      onClick={onSelect}
                      isHidden={itm.isHidden}
                      isDisabled={itm.isDisabled}
                    />
                  );
                })}
              </Stack>
            );
          }}
        />
      </Box>
      <FloatingBar
        onMouseEnter={() => interactiveToggle.setIsPanelInteractive(true)}
        onMouseLeave={() => interactiveToggle.setIsPanelInteractive(false)}
        isVisible={!!selectedFilter}
        ref={searchBoxRef}
      >
        {filterPanels.map((itm, idx) => {
          return (
            <FilterPanel key={idx} isVisible={selectedFilter == itm.name}>
              <Box p={3}>{itm.component}</Box>
              {itm.footerComponent && (
                <Box
                  borderTop={`1px solid ${theme.palette.divider}`}
                  px={3}
                  py={2}
                >
                  {itm.footerComponent}
                </Box>
              )}
            </FilterPanel>
          );
        })}
      </FloatingBar>
    </OutsideClickHandler>
  );
});

DesktopFilters.displayName = "DesktopFilters";

const debouncedClose = debounce();
function useFilterInteraction({
  onSelect,
  isDisabledInteractiveToggle,
}: DesktopFilterProps) {
  const [isSearchInteractive, setIsSearchInteractive] = React.useState(false);
  const [isPanelInteractive, setIsPanelInteractive] = React.useState(false);
  const { isScrolling } = useElementScroll(window);

  React.useEffect(() => {
    if (!isDisabledInteractiveToggle) {
      if (!isScrolling) {
        debouncedClose(() => {
          if (isSearchInteractive || isPanelInteractive) return;
          onSelect("");
        }, 1000);
      } else {
        onSelect("");
      }
    }
  }, [
    isSearchInteractive,
    isPanelInteractive,
    isScrolling,
    isDisabledInteractiveToggle,
  ]);

  return {
    setIsSearchInteractive,
    setIsPanelInteractive,
  };
}

interface FilterPanelProps {
  isVisible: boolean;
  children: React.ReactNode;
}

function FilterPanel({ isVisible, children }: FilterPanelProps) {
  const theme = useTheme();
  const styles = useStyles(theme, { isVisible });

  return (
    <Box sx={styles.filterPanel}>
      <Box>{children}</Box>
    </Box>
  );
}

function useStyles(theme: Theme, states?: { isVisible?: boolean }) {
  return {
    searchBoxWrapper: {
      width: "650px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
    filterPanel: {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "fit-content",
      pointerEvents: states?.isVisible ? "all" : "none",
      transform: states?.isVisible ? "scaleY(1)" : "scaleY(0)",
      transition: "all 0.3s ease",
      opacity: states?.isVisible ? 1 : 0,
      transformOrigin: "top",
      border: `1px solid ${theme.palette.divider}`,
      borderBottomLeftRadius: "8px",
      borderBottomRightRadius: "8px",
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0px 16px 32px 0px rgba(0,0,0,0.1)",
    },
  };
}
